import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import type { DTOSubscriptionPageInfo } from 'src/apis/dto/DTOSubscriptionPage';
import type { SubscriptionGuideLandFrom } from 'src/components/AzarOnly/Subscription/SubscriptionGuideModal';
import SubscriptionPaymentModal from 'src/components/AzarOnly/Subscription/SubscriptionPaymentModal';
import type { PriceInfo } from 'src/components/AzarOnly/Subscription/types';
import { findPurchasablePlan, getPlanTermsTexts, getSubscriptionByProductType, getSubscriptionExcludingProductType, getSubscriptionAction, getSubscriptionButtonTextKey, isPaymentModalAction, isDisabledAction, isManageAction, toPriceInfo } from 'src/components/AzarOnly/Subscription/utils';
import type { SubscriptionAction } from 'src/components/AzarOnly/Subscription/utils';
import type { PurchasablePlan } from 'src/components/AzarOnly/Subscription/utils/formatSubscription';
import type { PaymentAction } from 'src/components/AzarOnly/Subscription/utils/getPaymentButtonTextKey';
import { useActiveSubscriptions, useManagementSession, useSubscriptionEventData, useSubscriptionStatus } from 'src/hooks/useSubscription';
import { showModalAtom } from 'src/stores/modal/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalType } from 'src/types/Modal';
import { pushEvent } from 'src/utils/event-gateway';
interface Options {
  initialFeature?: string;
  landFrom: SubscriptionGuideLandFrom;
  defaultPlanId?: string;
}
interface SubscriptionButtonProps {
  textKey: string;
  disabled: boolean;
  planId: string;
  priceInfo?: PriceInfo;
  loading?: boolean;
}
interface Result {
  buttonProps: SubscriptionButtonProps | null;
  handleButtonClick: () => void;
}
export const useSubscriptionGuideButton = (selectedPlanId: string | undefined, pages: DTOSubscriptionPageInfo[], {
  initialFeature,
  landFrom,
  defaultPlanId
}: Options): Result => {
  const {
    i18n
  } = useTranslation();
  const subscriptionStatus = useSubscriptionStatus();
  const {
    data: activeSubscriptions,
    isLoading: isActiveSubscriptionsLoading
  } = useActiveSubscriptions();
  const subscriptionEventData = useSubscriptionEventData({
    pageId: selectedPlanId
  });
  const showModal = useSetAtom(showModalAtom);
  const {
    mutate: createManagementSession
  } = useManagementSession();
  const resolved = ((): {
    action: SubscriptionAction;
    buttonProps: SubscriptionButtonProps;
    plan: PurchasablePlan;
    priceInfo: PriceInfo;
  } | null => {
    if (!selectedPlanId) return null;
    const plan = findPurchasablePlan(pages, selectedPlanId);
    if (!plan) return null;
    const action = getSubscriptionAction({
      selectedPlan: selectedPlanId,
      subscriptionStatus
    });
    const priceInfo = toPriceInfo(plan.planModule, i18n.language);
    const textKey = getSubscriptionButtonTextKey(action, selectedPlanId);
    const buttonProps: SubscriptionButtonProps = isDisabledAction(action) ? {
      textKey,
      disabled: true,
      planId: selectedPlanId
    } : {
      textKey,
      disabled: false,
      planId: selectedPlanId,
      priceInfo
    };
    return {
      action,
      buttonProps: isActiveSubscriptionsLoading ? {
        ...buttonProps,
        disabled: true,
        loading: true
      } : buttonProps,
      plan,
      priceInfo
    };
  })();
  const handleButtonClick = () => {
    if (!resolved) return;
    const {
      action,
      plan,
      priceInfo
    } = resolved;
    pushEvent({
      eventType: EVENT_TYPE.SUBSCRIPTION,
      eventName: EVENT_NAME.SUBS__CLICK_BUTTON,
      eventParams: {
        button_type: action,
        current_page_id: selectedPlanId,
        initial_feature: initialFeature ?? 'none',
        initial_page_id: defaultPlanId ?? selectedPlanId,
        land_from: landFrom,
        ...subscriptionEventData.eventParams,
        is_store_account_active: false,
        is_store_account_matched: false
      }
    });
    if (isPaymentModalAction(action)) {
      // upgrade/downgrade: 현재 구독(타겟과 다른 productType)을 찾아서 변경
      // new: 새 구독이므로 현재 구독 없음
      const currentSubscription = (action === 'upgrade' || action === 'downgrade') && selectedPlanId ? getSubscriptionExcludingProductType(activeSubscriptions, selectedPlanId) : undefined;
      showModal({
        key: ModalType.SUBSCRIPTION_PAYMENT,
        component: () => <SubscriptionPaymentModal pageId={selectedPlanId ?? ''} initialPageId={defaultPlanId} landFrom={landFrom} initialFeature={initialFeature} modalTitle={plan.page.modalTitle ?? ''} planModule={plan.planModule} pgIntroductoryOfferEligible={plan.page.pgIntroductoryOfferEligible} termsTexts={getPlanTermsTexts(plan.page)} action={action as PaymentAction} priceInfo={priceInfo} productId={plan.planModule.productId} subscriptionId={currentSubscription?.subscriptionId} planId={plan.page.pageId} />
      });
    } else if (isManageAction(action)) {
      // renew: 같은 플랜을 갱신하므로 타겟과 같은 productType 찾기
      const subscription = selectedPlanId ? getSubscriptionByProductType(activeSubscriptions, selectedPlanId) : undefined;
      if (subscription) {
        createManagementSession(() => subscription.subscriptionId);
      }
    }
  };
  return {
    buttonProps: resolved?.buttonProps ?? null,
    handleButtonClick
  };
};